// src/stores/membership.js
import { defineStore } from "pinia";
import axios from "@/axios";

export const useMembershipStore = defineStore("membership", {
  state: () => ({
    levels: [],
    userProgress: null,
    missions: [],
    pointsHistory: [],
    membershipInfo: null,
    loading: false,
    error: null,
  }),

  getters: {
    hasMembership: (state) => !!state.membershipInfo?.has_membership,
    currentLevel: (state) => state.membershipInfo?.current_level || null,
    totalPoints: (state) => state.membershipInfo?.total_points || 0,
    nextLevel: (state) => {
      // Primero intentar obtenerlo directamente de la respuesta de la API
      if (state.membershipInfo?.level_progress?.next_level) {
        return state.membershipInfo.level_progress.next_level;
      }
      
      // Fallback a la lógica anterior
      if (!state.levels.length || !state.userProgress) return null;
      const currentPoints = state.userProgress.current_points;
      return state.levels.find(level => level.points_required > currentPoints) || null;
    },
    levelProgress: (state) => state.userProgress?.percentage || 0,
    pointsToNextLevel: (state) => state.userProgress?.points_needed || 0,
    availableMissions: (state) => state.missions || [],
  },

  actions: {
    async fetchLevels() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get("/v1/membership/levels");
        this.levels = response.data.levels;
        this.userProgress = response.data.user_progress;
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Error al cargar niveles";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchMissions() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get("/v1/membership/missions");
        this.missions = response.data.missions;
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Error al cargar misiones";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async completeMission(missionCode, metadata = {}) {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.post("/v1/membership/complete-mission", {
          mission_code: missionCode,
          metadata
        });
        
        // Actualizar información después de completar misión
        await this.fetchMembershipInfo();
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Error al completar misión";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchPointsHistory(page = 1, perPage = 10) {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get("/v1/membership/points-history", {
          params: { page, per_page: perPage }
        });
        this.pointsHistory = response.data;
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Error al cargar historial";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async activateMembership() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.post("/v1/membership/activate");
        await this.fetchMembershipInfo();
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Error al activar membresía";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchMembershipInfo() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get("/v1/membership/my-info");
        this.membershipInfo = response.data;
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Error al cargar membresía";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    clearError() {
      this.error = null;
    }
  },
});